import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOfflineStatus } from './useOfflineStatus';
import { useHydration } from './useHydration';
import { processSyncQueue, type SyncCallbacks } from '../lib/offline/syncEngine';
import { getAllFeatures } from '../lib/offline/operationRegistry';
import { useToast } from '../context/ToastContext';

export function useOfflineSync(isLoggedIn: boolean, userId: number | null) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isOnline, pendingCount, refreshPendingCount } = useOfflineStatus();

  useHydration(isLoggedIn, userId);

  // Gate on userId so we also fire once the Dexie handle is actually
  // open. On a cold PWA start the app renders with isLoggedIn already
  // true (localStorage) but user?.id only after React Query rehydrates
  // `auth-user`. Without userId in the dep array the effect ran once
  // with the DB still closed, syncEngine bailed silently on
  // getOfflineDb() returning null, and the queue sat there until the
  // next manual reload (VIVATISPDB-312 follow-up).
  useEffect(() => {
    if (!isOnline || !isLoggedIn || !userId) return;

    const callbacks: SyncCallbacks = {
      onSyncComplete: () => {
        refreshPendingCount();
        for (const feature of getAllFeatures()) {
          for (const key of feature.invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }
      },
      onSyncError: (failedCount) => {
        refreshPendingCount();
        toast.show(`${failedCount} item(s) couldn't sync`, 'error');
        for (const feature of getAllFeatures()) {
          for (const key of feature.invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }
      },
    };
    processSyncQueue(callbacks).then((hadItems) => {
      if (!hadItems) refreshPendingCount();
    });
  }, [isOnline, isLoggedIn, userId, queryClient, toast, refreshPendingCount]);

  return { isOnline, pendingCount };
}
